import React from "react";
import { Button, Text } from "@puzzle/ve";
import { CreditCardGradient, DollarArrows, External, BrandIcon, BrandName } from "@puzzle/icons";

import { useAppRouter } from "lib/useAppRouter";
import links from "lib/links";

import {
  rootStyle,
  containerStyle,
  innerContainerStyle,
  buttonRowStyle,
  logosContainerStyle,
} from "./TransactionConnectionsEmptyStateBase.css";

export const TransactionConnectionsEmptyStateBase = ({
  icon,
  title,
  content,
  learnMoreUrl,
  buttonText,
  buttonOnClick,
  logos,
}: {
  icon?: React.ReactElement;
  title: string;
  content: string;
  learnMoreUrl: string;
  buttonText: string;
  buttonOnClick: () => void;
  logos?: React.ReactElement;
}) => {
  return (
    <div className={rootStyle}>
      <div className={containerStyle}>
        {icon}
        <div className={innerContainerStyle}>
          <Text variant="heading3" weight="bold">
            {title}
          </Text>
          <Text variant="headingM" weight="bold" color="neutral-base">
            {content}
          </Text>
        </div>
        <div className={buttonRowStyle}>
          <Button
            variant="secondaryAlt"
            suffixElement={<External />}
            onClick={() => {
              window.open(learnMoreUrl, "_blank");
            }}
          >
            Learn more
          </Button>
          <Button variant="inverted" suffixElement={logos} onClick={buttonOnClick}>
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

const BRAND_LOGO_SIZE = 20;

export const LogoRow = () => {
  return (
    <div className={logosContainerStyle}>
      <BrandIcon name={BrandName.Mercury} width={BRAND_LOGO_SIZE} height={BRAND_LOGO_SIZE} />
      <BrandIcon name={BrandName.Ramp} width={BRAND_LOGO_SIZE} height={BRAND_LOGO_SIZE} />
      <BrandIcon name={BrandName.Brex} width={BRAND_LOGO_SIZE} height={BRAND_LOGO_SIZE} />
    </div>
  );
};

export const StripeLogo = () => {
  return (
    <div className={logosContainerStyle}>
      <BrandIcon name={BrandName.Stripe} width={BRAND_LOGO_SIZE} height={BRAND_LOGO_SIZE} />
    </div>
  );
};

export const TransactionConnectionsEmptyStateBankCC = () => {
  const { goToIntegrations } = useAppRouter();

  return (
    <TransactionConnectionsEmptyStateBase
      icon={<DollarArrows />}
      title="Transactions"
      content="Connect all your bank and credit accounts to bring in all your transactions and categorize them in Puzzle."
      learnMoreUrl={links.integrationsFaqs}
      buttonText="Connect financial accounts"
      buttonOnClick={() => {
        goToIntegrations();
      }}
      logos={<LogoRow />}
    />
  );
};

export const TransactionConnectionsEmptyStatePaymentProcessor = () => {
  const { goToIntegrations } = useAppRouter();

  return (
    <TransactionConnectionsEmptyStateBase
      icon={<CreditCardGradient />}
      title="Payment processor"
      content="Connect your payment processor to automate accounting of all your payments in Puzzle."
      learnMoreUrl={links.integrationsFaqs}
      buttonText="Connect Stripe"
      buttonOnClick={() => {
        goToIntegrations();
      }}
      logos={<StripeLogo />}
    />
  );
};

export const TransactionConnectionsEmptyStatePaymentProcessorSubledgerDisabled = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  return (
    <TransactionConnectionsEmptyStateBase
      icon={<CreditCardGradient />}
      title="Payment processor"
      content="Too many Stripe transactions to review? Enable the Payment Processor Subledger and Puzzle will post daily summary journal entries instead."
      learnMoreUrl={links.stripeSubledgerFaqs}
      buttonText="Enable payments subledger"
      buttonOnClick={onClick}
    />
  );
};

export const StatCardsNoConnectionsEmptyState = () => {
  const { goToIntegrations } = useAppRouter();

  return (
    <TransactionConnectionsEmptyStateBase
      title="Financial insights"
      content="Connect all your financial accounts to see real time insights into your Cash, Expenses, Revenue, and more."
      learnMoreUrl={links.integrationsFaqs}
      buttonText="Connect accounts"
      buttonOnClick={() => {
        goToIntegrations();
      }}
      logos={<LogoRow />}
    />
  );
};

export const FinancialStatementsEmptyState = () => {
  const { goToIntegrations } = useAppRouter();

  return (
    <TransactionConnectionsEmptyStateBase
      title="Financial statements"
      content="Connect all your financial accounts to see real time insights into your Cash activity, Balance sheet, and Profit and loss."
      buttonText="Connect accounts"
      buttonOnClick={() => {
        goToIntegrations();
      }}
      logos={<LogoRow />}
      learnMoreUrl={links.integrationsFaqs}
    />
  );
};
